import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import SettingsOptions from '../../common/SettingsOptions';
import { MysteryShopperAddressDto, MysteryShopperAddressType, MysteryShopperClient, PostalAddressDto } from '../../../api/api.g';
import AddressForm, { AddressFormValues } from '../../address/AddressForm';
import { useAppDispatch } from '../../../hooks/store-hook';
import useTranslation from '../../../hooks/translation-hook';
import useApi from '../../../hooks/api-hook';
import useUserPrefsService from '../../../hooks/userPrefsService-hook';
import { Modal, Platform, Pressable, View } from 'react-native';
import Label, { LabelType } from '../../common/Label';
import useStyles from '../../../hooks/styles-hook';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import ProfileModal from './ProfileModal';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faMapLocationDot } from '@fortawesome/pro-regular-svg-icons';
import useCustomTheme from '../../../hooks/theme-hook';
import useAddressIcon from '../../../hooks/addressIcon-hook';
import useGoogleValidation from '../../../hooks/googleValidation-hook';
import Overlay from '../../common/Overlay';
import Popup from '../../common/Popup';
import ModalViewport from '../../common/ModalViewport';

export interface AddressItemProps extends AddressFormValues {
  option: string;
  addButton?: boolean;
  readonly?: boolean;
}

const AddressItem = (props: AddressItemProps) => {
  const dispatch = useAppDispatch();
  const t = useTranslation();
  const theme = useCustomTheme();
  const userPrefService = useUserPrefsService();
  const apiMysteryShopper = useApi(MysteryShopperClient);
  const styles = useStyles((section) => section.settingsOptions);
  const addressIcon = useAddressIcon(20, theme.text.titleDark);
  const googleValidator = useGoogleValidation();
  const isWeb = Platform.OS === 'web';

  const [dto, setDto] = useState<MysteryShopperAddressDto>(undefined);
  const [confirmed, setConfirmed] = useState(false);
  const [forcedConfirm, setForcedConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState<string | ReactElement>('');
  const [suspiciousAddress, setSuspiciousAddress] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  const addressValues = useMemo<AddressFormValues>(
    () => ({
      id: props.id,
      city: props.city,
      postalCode: props.postalCode,
      address: props.address,
      country: props.country,
      department: props.department,
      type: props.type,
      latitude: props.latitude,
      longitude: props.longitude,
    }),
    [props.address, props.city, props.country, props.department, props.id, props.latitude, props.longitude, props.postalCode, props.type]
  );

  const closeAddressModal = () => {
    setIsAddressModalOpen(false);
    setLoading(false);

    if (isWeb) {
      dispatch(lightBoxActions.setIsLoading(false));
      dispatch(lightBoxActions.setContent(<></>));
      dispatch(lightBoxActions.setVisibility(false));
    }
  };

  const handleOnConfirm = () => {
    if (dto && dto.address && dto.city && dto.postalCode && dto.department && dto.country) {
      var data = new PostalAddressDto();

      data.civicAddress = dto.address;
      data.city = dto.city;
      data.postalCode = dto.postalCode;
      data.department = dto.department;
      data.country = dto.country;

      googleValidator(data, (valid) => {
        if (valid) {
          setSuspiciousAddress(false);
          setConfirmed(true);
        } else {
          setSuspiciousAddress(true);
          setShowConfirm(true);
        }
      });
    } else {
      setSuspiciousAddress(true);
    }
  };

  const renderAddressModal = (address: AddressFormValues) => (
    <Popup loading={!isWeb && loading} scrollable={false} screenMargins={!isWeb}>
      <ProfileModal
        scrollCloseButton
        onClose={closeAddressModal}
        showDelete={!props.addButton}
        title={t('address')}
        onConfirm={handleOnConfirm}
        onDelete={() => {
          setLoading(true);
          if (isWeb) {
            dispatch(lightBoxActions.setIsLoading(true));
          }

          apiMysteryShopper
            .deleteAddress(address.id)
            .then((result) =>
              userPrefService.updateUserProfileInfo().then(() => {
                closeAddressModal();
              })
            )
            .catch(() => {
              setLoading(false);
              if (isWeb) {
                dispatch(lightBoxActions.setIsLoading(false));
              }
            });
        }}
      >
        <>
          {suspiciousAddress && (
            <Label style={{ marginBottom: 24, color: theme.text.error, fontSize: 16, fontWeight: '600', flexShrink: 1, maxWidth: 260 }} numberOfLines={5}>
              {t('address-incorrect-check-again')}
            </Label>
          )}
          <AddressForm
            id={address.id}
            city={address.city}
            postalCode={address.postalCode}
            address={address.address}
            department={address.department}
            country={address.country}
            type={address.type}
            onChange={(result, valid) => {
              setDto((prev) => {
                const newDto = new MysteryShopperAddressDto();
                newDto.init(result);
                return newDto;
              });
            }}
          />

          {showConfirm && (
            <Pressable
              onPress={() => {
                setConfirmed(true);
                setShowConfirm(false);
                setSuspiciousAddress(false);
              }}
            >
              <Label style={{ color: theme.text.darkTurquoiseTwo, fontSize: 12, fontWeight: '600', flexShrink: 1 }}>
                {t('Is-this-the-correct-address-Continue-with-this-add')}
              </Label>
            </Pressable>
          )}
        </>
      </ProfileModal>
    </Popup>
  );

  const handleAddressManager = () => {
    setSuspiciousAddress(false);
    setShowConfirm(false);
    setIsAddressModalOpen(true);

    if (isWeb) {
      dispatch(lightBoxActions.setContent(renderAddressModal(addressValues)));
      dispatch(lightBoxActions.setVisibility(true));
    }
  };

  useEffect(() => {
    if (!isAddressModalOpen || !isWeb) {
      return;
    }

    dispatch(lightBoxActions.setContent(renderAddressModal(addressValues)));
  }, [isAddressModalOpen, suspiciousAddress, showConfirm, isWeb]);

  useEffect(() => {
    if (props.type !== undefined) {
      if (props.type === MysteryShopperAddressType.Current) {
        setTitle(
          <View style={{ flexDirection: 'row' }}>
            <FontAwesomeIcon icon={faMapLocationDot} style={{ marginRight: 4, color: theme.text.titleDark.toString() }} />
            <Label variant={LabelType.titleSmall} style={styles.text.title}>
              {t(MysteryShopperAddressType[props.type].toString().toLowerCase())}
            </Label>
          </View>
        );
      } else {
        setTitle(t(MysteryShopperAddressType[props.type].toString().toLowerCase()));
      }
    } else {
      setTitle('');
    }
  }, [props.type]);

  useEffect(() => {
    if (confirmed) {
      setLoading(true);
      if (isWeb) {
        dispatch(lightBoxActions.setIsLoading(true));
      }

      if (props.id) {
        apiMysteryShopper
          .updateAddress(props.id, dto)
          .then((result) => {
            userPrefService.updateUserProfileInfo().then(() => {
              closeAddressModal();
            });
          })
          .catch(() => {
            setLoading(false);
            if (isWeb) {
              dispatch(lightBoxActions.setIsLoading(false));
            }
          })
          .finally(() => setConfirmed(false));
      } else {
        apiMysteryShopper
          .addAddress(dto)
          .then((result) => {
            userPrefService.updateUserProfileInfo().then(() => {
              closeAddressModal();
            });
            setConfirmed(false);
          })
          .catch(() => {
            setLoading(false);
            if (isWeb) {
              dispatch(lightBoxActions.setIsLoading(false));
            }
          })
          .finally(() => setConfirmed(false));
      }
    }
  }, [confirmed]);

  return (
    <>
      {!isWeb && (
        <Modal
          animationType="none"
          visible={isAddressModalOpen}
          transparent={true}
          hardwareAccelerated
          presentationStyle="overFullScreen"
          onRequestClose={closeAddressModal}
        >
          <ModalViewport>
            <Overlay showOverLay={true} zIndex={1998} onPress={closeAddressModal}>
              {renderAddressModal(addressValues)}
            </Overlay>
          </ModalViewport>
        </Modal>
      )}
      {!props.addButton && (
        <SettingsOptions readonly={props.readonly} bottomBorder icon={addressIcon(props.type)} title={title} option={props.option} onPress={handleAddressManager} />
      )}
      {props.addButton && (
        <Pressable style={[styles.container, styles.addButton]} onPress={handleAddressManager}>
          {props.option && <Label style={styles.addButtonLabel}>{props.option}</Label>}
        </Pressable>
      )}
    </>
  );
};

export default AddressItem;

